import './ManageErpIntegration.scss';
import React, { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { getPartnerName, Partner } from 'features/settings/importMapping/partner';
import { Button, Card, Form, OverlayTrigger, Popover } from 'react-bootstrap';
import ErpIntegration from 'domain/erpIntegration';
import { ErpSystem, getErpSystemName, getErpSystemsForPartner } from 'domain/erpSystem';
import AsyncButton from 'common/ui/AsyncButton';
import ErpImportModal from './import/ErpImportModal';
import NumberEditor from 'common/ui/NumberEditor';
import TypedSelect from 'common/ui/TypedSelect';
import classNames from 'classnames';
import moment from 'moment';

export interface ManageErpIntegrationProps {
    erpIntegration: ErpIntegration;
    partners: Partner[];
    onSave(erpIntegration: ErpIntegration): Promise<any>;
    onRemove(): Promise<any>;
}

const partnersWithErpIntegration = [Partner.AgTrax, Partner.Agris, Partner.Oakland, Partner.Agvance, Partner.EverAg, Partner.AgVision, Partner.AgVantage, Partner.AgTech, Partner.Intellego];

const ManageErpIntegration: React.FC<ManageErpIntegrationProps> = props => {

    const [erpIntegration, setErpIntegration] = useState<ErpIntegration>(null);
    const [supportedPartners, setSupportedPartners] = useState<Partner[]>(null);
    const [supportedErps, setSupportedErps] = useState<ErpSystem[]>(null);
    const [isNew, setIsNew] = useState(false);

    useEffect(() => {
        setSupportedPartners(_.intersection(partnersWithErpIntegration, props.partners));
    }, [props.partners]);

    useEffect(() => {
        setErpIntegration(props.erpIntegration);
        setIsNew(props.erpIntegration == null);
    }, [props.erpIntegration]);

    useEffect(() => {
        const erps = erpIntegration?.partner == null
        ? []
        : getErpSystemsForPartner(erpIntegration.partner);
        setSupportedErps(erps);

        if (erps.length === 1) {
            setErpIntegration({...erpIntegration, erpSystem: erps[0]})
        }
    }, [erpIntegration?.partner])

    function handleAdd() {
        setErpIntegration({
            ...new ErpIntegration(),
            isEnabled: false,
            enableContractWriteBack: false,
            partner: supportedPartners.length === 1 ? supportedPartners[0] : null
        });
    }

    function handleSave(): Promise<any> {
        return props.onSave(erpIntegration);
    }

    function handleCancel(): void {
        setErpIntegration(props.erpIntegration);
    }

    function handleRemove(): void {
        props.onRemove();
    }

    return <IntegrationDetails
        erpIntegration={erpIntegration}
        partners={supportedPartners}
        erpSystems={supportedErps}
        startEditing={handleAdd}
        update={setErpIntegration}
        onSave={handleSave}
        onCancel={handleCancel}
        onRemove={handleRemove}
        isNew={isNew}
        canImport={props.erpIntegration?.isEnabled} />
}

interface IntegrationDetailsProps {
    erpIntegration: ErpIntegration;
    partners: Partner[];
    erpSystems: ErpSystem[];
    startEditing(): void;
    update(integration: ErpIntegration): void;
    onSave(): Promise<any>;
    onCancel(): void;
    onRemove(): void;
    isNew: boolean;
    canImport: boolean;
}

const IntegrationDetails: React.FC<IntegrationDetailsProps> = props => {

    const [hasChanges, setHasChanges] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [formDetails, setFormDetails] = useState<FormDetails>(null);

    useEffect(() => {
        const details: FormDetails = {
            showErpSystem: false,
            showImportData: true,
            showContractPolling: false,
            showCommodityBalancePolling: false,
            showScaleTicketPolling: false,
            showSettlementPolling: false,
            showAccountPolling: false,
            showContractImport: true,
            showHedgeOrderExport: false,
            showExcludeContractsBeforeDate: false,
            showCustomerTypes: false,
            showInventoryLocationEditable: false,
            showContractExportThrottling: false
        };

        switch (props.erpIntegration?.partner) {
            case Partner.Agris:
                details.showContractPolling = true;
                details.showCommodityBalancePolling = true;
                details.showHedgeOrderExport = true;
                details.pollingFrequencyStep = 60;
                details.showExcludeContractsBeforeDate = true;
                details.showScaleTicketPolling = true;
                details.showSettlementPolling = true;
                details.showAccountPolling = true;
                details.showCustomerTypes = true;
                details.showInventoryLocationEditable = true;
                details.showContractExportThrottling = true;
                break;
            case Partner.Oakland:
                details.showCommodityBalancePolling = true;
                details.showContractImport = false;
                details.showHedgeOrderExport = true;
                details.pollingFrequencyStep = 30;
                details.showScaleTicketPolling = true;
                break;
            case Partner.Agvance:
                details.pollingFrequencyStep = 60;
                details.showContractPolling = true;
                details.showCommodityBalancePolling = false;
                details.showScaleTicketPolling = true;
                details.showSettlementPolling = true;
                details.showHedgeOrderExport = true;
                details.showInventoryLocationEditable = true;
                break;
            case Partner.EverAg:
                details.pollingFrequencyStep = 60;
                details.showCommodityBalancePolling = true;
                details.showContractPolling = true;
                details.showScaleTicketPolling = true;
                details.showSettlementPolling = true;
                details.showHedgeOrderExport = true;
                break;
            case Partner.AgVision:
                details.pollingFrequencyStep = 60;
                details.showContractImport = true;
                details.showContractPolling = true;
                details.showCommodityBalancePolling = true;
                details.showScaleTicketPolling = true;
                details.showSettlementPolling = true;
                details.showAccountPolling = true;
                break;
            case Partner.AgVantage:
                details.pollingFrequencyStep = 60;
                details.showContractPolling = true;
                details.showScaleTicketPolling = true;
                details.showSettlementPolling = true;
                details.showContractImport = true;
                details.showCommodityBalancePolling = true;
                break;
            case Partner.AgTech:
                details.showImportData = false;
                details.showContractPolling = false;
                details.showContractImport = false;
                break;
            case Partner.Intellego:
                details.pollingFrequencyStep = 60;
                details.showContractPolling = true;
                details.showScaleTicketPolling = true;
                details.showSettlementPolling = true;
                details.showContractImport = true;
                details.showCommodityBalancePolling = true;
                break;
        }
        setFormDetails(details);
    }, [props.erpIntegration?.partner])

    function enableContractPolling(enabled: boolean) {
        const newIntegration = {...props.erpIntegration, enableContractPolling: enabled};
        if (formDetails?.pollingFrequencyStep != null && enabled &&
            !(props.erpIntegration.contractPollingFrequencyInSeconds > 0)) {
                newIntegration.contractPollingFrequencyInSeconds = formDetails.pollingFrequencyStep;
            }
        update(newIntegration);
    }

    function enableCommodityBalancePolling(enabled: boolean) {
        const newIntegration = {...props.erpIntegration, enableCommodityBalancePolling: enabled};
        if (formDetails?.pollingFrequencyStep != null && enabled &&
            !(props.erpIntegration.commodityBalancePollingFrequencyInSeconds > 0)) {
                newIntegration.commodityBalancePollingFrequencyInSeconds = formDetails.pollingFrequencyStep;
            }
        update(newIntegration);
    }

    function enableScaleTicketPolling(enabled: boolean) {
        const newIntegration = {...props.erpIntegration, enableScaleTicketPolling: enabled};
        if (formDetails?.pollingFrequencyStep != null && enabled &&
            !(props.erpIntegration.scaleTicketPollingFrequencyInSeconds > 0)) {
                newIntegration.scaleTicketPollingFrequencyInSeconds = formDetails.pollingFrequencyStep;
            }
        update(newIntegration);
    }

    function enableSettlementPolling(enabled: boolean) {
        const newIntegration = {...props.erpIntegration, enableSettlementPolling: enabled};
        if (formDetails?.pollingFrequencyStep != null && enabled &&
            !(props.erpIntegration.settlementPollingFrequencyInSeconds > 0)) {
                newIntegration.settlementPollingFrequencyInSeconds = formDetails.pollingFrequencyStep;
            }
        update(newIntegration);
    }

    function enableAccountPolling(enabled: boolean) {
        const newIntegration = {...props.erpIntegration, enableAccountPolling: enabled};
        if (formDetails?.pollingFrequencyStep != null && enabled &&
            !(props.erpIntegration.accountPollingFrequencyInSeconds > 0)) {
                newIntegration.accountPollingFrequencyInSeconds = formDetails.pollingFrequencyStep;
            }
        update(newIntegration);
    }

    function normalizePollingFrequency(seconds: number): number {
        if (formDetails.pollingFrequencyStep == null) return seconds;
        return seconds - (seconds % formDetails.pollingFrequencyStep);
    }

    async function update(integration: ErpIntegration) {
        props.update(integration);
        setHasChanges(true);
    }

    function canSave(): boolean {
        return props.erpIntegration?.partner != null &&
            props.erpIntegration?.erpSystem != null;
    }

    async function onSave(): Promise<any> {
        await props.onSave();
        setHasChanges(false);
    }

    function onCancel() {
        props.onCancel();
        setHasChanges(false);
    }

    if (props.partners?.length === 0) {
        return <Card className="mb-4 integration-details">
                <Card.Body>
                    <Card.Title>ERP</Card.Title>
                    <Card.Text>No configured partners allow for ERP integration</Card.Text>
                </Card.Body>
            </Card>;
    }

    return <Card className="mb-4 integration-details">
        <Card.Body>
            <Card.Title>
                ERP
                {props.erpIntegration == null &&
                    <button className="link-button" onClick={() => props.startEditing()}><i className="material-icons">add</i></button>
                }
                {props.erpIntegration != null && !props.isNew &&
                    <button className="link-button" onClick={() => props.onRemove()}><i className="material-icons">delete</i></button>
                }
            </Card.Title>
            {props.erpIntegration && formDetails && <div>
                <div>
                    <Form.Label>Partner</Form.Label>
                    <Form.Control as="select"
                        value={props.erpIntegration.partner}
                        disabled={!props.isNew}
                        onChange={ev => update({...props.erpIntegration, partner: Number.parseInt(ev.target.value)})}>
                        {props.erpIntegration.partner == null && <option>--Select Partner---</option>}
                        {props.partners.map(m => <option key={m} value={m}>{getPartnerName(m)}</option>)}
                    </Form.Control>
                </div>
                {formDetails.showErpSystem &&
                <div>
                    <Form.Label>ERP System</Form.Label>
                    <Form.Control as="select"
                        value={props.erpIntegration.erpSystem}
                        disabled={!props.isNew}
                        onChange={ev => update({...props.erpIntegration, erpSystem: Number.parseInt(ev.target.value)})}>
                        {props.erpSystems.map(m => <option key={m} value={m}>{getErpSystemName(m)}</option>)}
                    </Form.Control>
                </div>
                }

                <div className="form-inline">
                    <Form.Label className="pr-2">Enable</Form.Label>
                    <Form.Check
                        type="switch"
                        id="enable-erp-import-switch"
                        checked={props.erpIntegration.isEnabled}
                        onChange={ev => update({...props.erpIntegration, isEnabled: ev.target.checked})}
                    />
                    <span className="small">This should be disabled until all mappings have been set up</span>
                </div>


                {formDetails.showImportData && !props.isNew &&
                <div className="mt-1">
                    <Button disabled={!props.canImport}
                        onClick={() => setShowImportModal(true)}>
                            Import data...
                    </Button>
                </div>}
                {showImportModal && <ErpImportModal
                    merchantId={props.erpIntegration.merchantId}
                    partner={props.erpIntegration.partner}
                    onHide={() => setShowImportModal(false)}
                />}

                {formDetails.showCustomerTypes && <>
                    <h5 className="pt-4">Customer Types <OverlayTrigger placement="right" overlay={CustomerTypesPopover}>
                                <i className="fa fa-info-circle pl-2"></i>
                            </OverlayTrigger></h5>

                    <div className="form-inline">
                        <Form.Control
                            type="text"
                            placeholder="Add comma separated list for allowed customer types like F,C,S"
                            id="allowed-customer-types"
                            value={props.erpIntegration.erpConfiguration?.allowedCustomerTypes}
                            onChange={ev => update({...props.erpIntegration, erpConfiguration: { ...props.erpIntegration.erpConfiguration, allowedCustomerTypes: ev.target.value }})}
                        />
                    </div>
                </>}

                <h5 className="pt-4">Contracts</h5>

                <div className="form-inline">
                    {formDetails.showContractImport && <>
                        <Form.Label className="pr-2">Import</Form.Label>
                        <Form.Check
                            type="switch"
                            id="enable-contract-import-switch"
                            checked={props.erpIntegration.enableContractImport}
                            onChange={ev => update({...props.erpIntegration, enableContractImport: ev.target.checked})}
                        />
                    </>}
                    {formDetails.showExcludeContractsBeforeDate && props.erpIntegration.enableContractImport && <>
                    <Form.Label className={classNames({"pl-2": formDetails.showContractImport},  "pr-2")}>Exclude Contracts Before</Form.Label>
                    <Form.Control type="date"
                        value={props.erpIntegration.erpConfiguration?.excludeBefore}
                        required
                        onChange={ev => update({...props.erpIntegration, erpConfiguration: {...props.erpIntegration.erpConfiguration, excludeBefore: moment(ev.target.value).format("YYYY-MM-DD")}})}>
                    </Form.Control></>}
                </div>
                <div className="form-inline">
                    <Form.Label className="pr-2">Export</Form.Label>
                    <Form.Check
                        type="switch"
                        id="enable-contract-writeback-switch"
                        checked={props.erpIntegration.enableContractWriteBack}
                        onChange={ev => update({...props.erpIntegration, enableContractWriteBack: ev.target.checked})}
                    />
                    {formDetails.showContractExportThrottling && props.erpIntegration.enableContractWriteBack && <>
                        <Form.Label className="pr-2">Throttle Requests</Form.Label>
                        <Form.Check
                            type="switch"
                            id="enable-contract-export-throttling-switch"
                            checked={props.erpIntegration.enableContractExportThrottling}
                            onChange={ev => update({...props.erpIntegration, enableContractExportThrottling: ev.target.checked})}
                        /></>
                    }
                </div>

                {formDetails.showContractPolling &&
                <div className="form-inline">
                    <Form.Label className="pr-2">
                        Polling
                        <OverlayTrigger placement="right" overlay={PollingPopover}>
                            <i className="fa fa-info-circle pl-2"></i>
                        </OverlayTrigger>
                    </Form.Label>
                    <Form.Check
                        type="switch"
                        id="enable-contract-poll-switch"
                        checked={props.erpIntegration.enableContractPolling}
                        onChange={ev => enableContractPolling(ev.target.checked)}
                    />

                    {props.erpIntegration.enableContractPolling && <>
                        <Form.Label className="pl-2 pr-2">Frequency</Form.Label>
                        <NumberEditor className="frequency-editor"
                            min={formDetails.pollingFrequencyStep}
                            unit="seconds"
                            value={props.erpIntegration.contractPollingFrequencyInSeconds}
                            onValueChange={val => update({...props.erpIntegration, contractPollingFrequencyInSeconds: normalizePollingFrequency(val)})}
                            />
                    </>}
                </div>}

                {formDetails.showInventoryLocationEditable && <>
                    <div className="form-inline">
                        <Form.Label className="pr-2">Allow Inventory Location Editing</Form.Label>
                        <Form.Check
                            type="switch"
                            id="is-inventory-location-editing-allowed-switch"
                            checked={props.erpIntegration.isInventoryLocationEditingAllowed}
                            onChange={ev => update({...props.erpIntegration, isInventoryLocationEditingAllowed: ev.target.checked})}
                        />
                    </div>
                </>}

                <ErpContractConfiguration
                    erpIntegration={props.erpIntegration}
                    onUpdate={config => update({...props.erpIntegration, erpConfiguration: config})}
                />

                {formDetails.showCommodityBalancePolling && <>
                    <h5 className="pt-4">Commodity Balances</h5>


                    <div className="form-inline">
                        <Form.Label className="pr-2">
                            Polling
                            <OverlayTrigger placement="right" overlay={PollingPopover}>
                                <i className="fa fa-info-circle pl-2"></i>
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Check
                            type="switch"
                            id="enable-commodity-balance-poll-switch"
                            checked={props.erpIntegration.enableCommodityBalancePolling}
                            onChange={ev => enableCommodityBalancePolling(ev.target.checked)}
                        />

                        {props.erpIntegration.enableCommodityBalancePolling && <>
                            <Form.Label className="pl-2 pr-2">Frequency</Form.Label>
                            <NumberEditor className="frequency-editor"
                                unit="seconds"
                                min={formDetails.pollingFrequencyStep}
                                value={props.erpIntegration.commodityBalancePollingFrequencyInSeconds}
                                onValueChange={val => update({...props.erpIntegration, commodityBalancePollingFrequencyInSeconds: normalizePollingFrequency(val)})}
                                />
                        </>}
                    </div>
                </>}

                {formDetails.showScaleTicketPolling && <>
                    <h5 className="pt-4">Scale Tickets</h5>


                    <div className="form-inline">
                        <Form.Label className="pr-2">
                            Polling
                            <OverlayTrigger placement="right" overlay={PollingPopover}>
                                <i className="fa fa-info-circle pl-2"></i>
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Check
                            type="switch"
                            id="enable-scale-ticket-poll-switch"
                            checked={props.erpIntegration.enableScaleTicketPolling}
                            onChange={ev => enableScaleTicketPolling(ev.target.checked)}
                        />

                        {props.erpIntegration.enableScaleTicketPolling && <>
                            <Form.Label className="pl-2 pr-2">Frequency</Form.Label>
                            <NumberEditor className="frequency-editor"
                                unit="seconds"
                                min={formDetails.pollingFrequencyStep}
                                value={props.erpIntegration.scaleTicketPollingFrequencyInSeconds}
                                onValueChange={val => update({...props.erpIntegration, scaleTicketPollingFrequencyInSeconds: normalizePollingFrequency(val)})}
                                />
                        </>}
                    </div>
                </>}


                {formDetails.showSettlementPolling && <>
                    <h5 className="pt-4">Settlements</h5>


                    <div className="form-inline">
                        <Form.Label className="pr-2">
                            Polling
                            <OverlayTrigger placement="right" overlay={PollingPopover}>
                                <i className="fa fa-info-circle pl-2"></i>
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Check
                            type="switch"
                            id="enable-settlement-poll-switch"
                            checked={props.erpIntegration.enableSettlementPolling}
                            onChange={ev => enableSettlementPolling(ev.target.checked)}
                        />

                        {props.erpIntegration.enableSettlementPolling && <>
                            <Form.Label className="pl-2 pr-2">Frequency</Form.Label>
                            <NumberEditor className="frequency-editor"
                                unit="seconds"
                                min={formDetails.pollingFrequencyStep}
                                value={props.erpIntegration.settlementPollingFrequencyInSeconds}
                                onValueChange={val => update({...props.erpIntegration, settlementPollingFrequencyInSeconds: normalizePollingFrequency(val)})}
                                />
                        </>}
                    </div>
                </>}


                {formDetails.showAccountPolling && <>
                    <h5 className="pt-4">Accounts</h5>


                    <div className="form-inline">
                        <Form.Label className="pr-2">
                            Polling
                            <OverlayTrigger placement="right" overlay={PollingPopover}>
                                <i className="fa fa-info-circle pl-2"></i>
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Check
                            type="switch"
                            id="enable-account-poll-switch"
                            checked={props.erpIntegration.enableAccountPolling}
                            onChange={ev => enableAccountPolling(ev.target.checked)}
                        />

                        {props.erpIntegration.enableAccountPolling && <>
                            <Form.Label className="pl-2 pr-2">Frequency</Form.Label>
                            <NumberEditor className="frequency-editor"
                                unit="seconds"
                                min={formDetails.pollingFrequencyStep}
                                value={props.erpIntegration.accountPollingFrequencyInSeconds}
                                onValueChange={val => update({...props.erpIntegration, accountPollingFrequencyInSeconds: normalizePollingFrequency(val)})}
                                />
                                
                            <Form.Label className="pl-2 pr-2">Auto Import</Form.Label>
                            <Form.Check
                                type="switch"
                                id="enable-account-auto-import-switch"
                                checked={props.erpIntegration.enableAccountAutoImport}
                                onChange={ev => update({...props.erpIntegration, enableAccountAutoImport: ev.target.checked})}
                            />
                                
                        </>}
                    </div>
                </>}

                {formDetails.showHedgeOrderExport &&  <>

                    <h5 className="pt-4">Hedge Orders</h5>
                    <div className="form-inline">
                        <Form.Label className="pr-2">Export</Form.Label>
                        <Form.Check
                            type="switch"
                            id="hedge-export-switch"
                            checked={props.erpIntegration.enableHedgeOrderExport}
                            onChange={ev => update({...props.erpIntegration, enableHedgeOrderExport: ev.target.checked})}
                        />
                    </div>
                </>}

            </div>}
        </Card.Body>
        {props.erpIntegration && (props.isNew || hasChanges) && <Card.Footer className="text-right">
            <Button variant="secondary" className="mr-2" onClick={onCancel}>Cancel</Button>
            <AsyncButton disabled={!canSave()} onClickAsync={onSave}>Save</AsyncButton>
        </Card.Footer>}
    </Card>

}

interface ErpContractConfigurationProps {
    erpIntegration: ErpIntegration;
    onUpdate(configuration: any): void;
}

const ErpContractConfiguration: React.FC<ErpContractConfigurationProps> = props => {
    switch (props.erpIntegration.partner) {
        case Partner.AgTrax:
            return <ExtendedFees {...props} />;
        case Partner.Agris:
            return <AgrisContractConfiguration {...props} />;
        case Partner.Oakland:
            return <OaklandContractConfiguration {...props} />;
        case Partner.Agvance:
            return <AgvanceContractConfiguration {...props} />;
        case Partner.AgVantage:
            return <ExtendedFees  {...props} />;
        default:
            return <></>;
    }
}

const AgrisFeeFields = [
    "brokerageprice",
    "otherprice",
    ...Array.from({length: 12}, (m, i) => "miscprice" + (i + 1)) //Generate miscprice1, miscprice2, etc.
];

const AgrisRemarksFields = [
    "Pricing Remarks",
    "Contracts Remarks"
];

const AgrisContractConfiguration: React.FC<ErpContractConfigurationProps> = props => {
    return <>
    <div className="form-inline">
        <Form.Label className="pr-2">Use Apply From</Form.Label>
        <Form.Check
            type="switch"
            id="use-apply-from-switch"
            checked={props.erpIntegration.erpConfiguration?.useApplyFrom}
            onChange={ev => props.onUpdate({...props.erpIntegration.erpConfiguration, useApplyFrom: ev.target.checked})}
        />
    </div>
    <PriceAsSameType {...props} />
    {props.erpIntegration.erpConfiguration?.feeField == null && <ExtendedFees {...props} />}
    <div className="form-inline">
        <Form.Label className="pr-2">Fee Field</Form.Label>
        <TypedSelect
            options={AgrisFeeFields}
            value={props.erpIntegration.erpConfiguration?.feeField}
            valueGetter={m => m}
            textGetter={m => m}
            showNullRow
            nullRowText="None"
            onValueSelected={val => props.onUpdate({...props.erpIntegration.erpConfiguration, feeField: val,
                useExtendedFees: val != null ? false : props.erpIntegration.erpConfiguration?.useExtendedFees})}
            />
    </div>
    <IncludeFeesInFinalPrice {...props} />
    <div className="form-inline">
        <Form.Label className="pr-2">Contract Comments Field</Form.Label>
        <TypedSelect
            options={AgrisRemarksFields}
            value={props.erpIntegration.erpConfiguration?.contractCommentsField}
            valueGetter={m => m}
            textGetter={m => m}
            showNullRow
            nullRowText="None"
            onValueSelected={val => props.onUpdate({...props.erpIntegration.erpConfiguration, contractCommentsField: val})}
            />
    </div>
    <div className="form-inline">
        <Form.Label className="pr-2">Customer Phone Field</Form.Label>
        <Form.Control type="text"
            id="customer-phone-field"
            value={props.erpIntegration.erpConfiguration?.customerPhoneField}
            onChange={event => props.onUpdate({...props.erpIntegration.erpConfiguration, customerPhoneField: event.target.value})}             
        />
    </div>
    <div className="form-inline">
        <Form.Label className="pr-2">Email Field</Form.Label>
        <Form.Control type="text"
            id="email-field"
            value={props.erpIntegration.erpConfiguration?.emailField}
            onChange={event => props.onUpdate({...props.erpIntegration.erpConfiguration, emailField: event.target.value})}             
        />
    </div>
    </>
}

const OaklandContractConfiguration: React.FC<ErpContractConfigurationProps> = props => {
    return <IncludeFeesInFinalPrice {...props} />
}

const AgvanceContractConfiguration: React.FC<ErpContractConfigurationProps> = props => {
    return <>
    <div className="form-inline">
        <Form.Label className="pr-2">Send Inventory Location as Bid Criteria</Form.Label>
        <Form.Check
            type="switch"
            id="send-inventory-location-as-bid-criteria-switch"
            checked={props.erpIntegration.erpConfiguration?.sendInventoryLocationAsBidCriteria}
            onChange={ev => props.onUpdate({...props.erpIntegration.erpConfiguration, sendInventoryLocationAsBidCriteria: ev.target.checked})}
        />
    </div>
    <IncludeFeesInFinalPriceForContractTypes {...props} />
    </>
}

const ExtendedFees: React.FC<ErpContractConfigurationProps> = props => {
    return <div className="form-inline">
    <Form.Label className="pr-2">Use Extended Fees</Form.Label>
    <Form.Check
        type="switch"
        id="use-extended-fees-switch"
        checked={props.erpIntegration.erpConfiguration?.useExtendedFees}
        onChange={ev => props.onUpdate({...props.erpIntegration.erpConfiguration, useExtendedFees: ev.target.checked})}
    />
    </div>
}

const IncludeFeesInFinalPrice: React.FC<ErpContractConfigurationProps> = props => {
    return <div className="form-inline">
    <Form.Label className="pr-2">Include Fees in Final Price</Form.Label>
    <Form.Check
        type="switch"
        id="include-fees-in-final-price-switch"
        checked={props.erpIntegration.erpConfiguration?.includeFeesInFinalPrice}
        onChange={ev => props.onUpdate({...props.erpIntegration.erpConfiguration, includeFeesInFinalPrice: ev.target.checked})}
    />
    </div>
}

const PriceAsSameType: React.FC<ErpContractConfigurationProps> = props => {
    return <div>
    <div className="row">
        <div className="col-4"></div>
        <div className="col"><strong>HTA</strong></div>
        <div className="col"><strong>Basis</strong></div>
        <div className="col"><strong>Delayed Price</strong></div>
    </div>
    <div className="row">
        <div className="col-4">
            <strong>Price as same type</strong>
            <OverlayTrigger placement="right" overlay={PriceAsSameTypePopover}>
                <i className="fa fa-info-circle pl-2"></i>
            </OverlayTrigger>
        </div>
        <div className="col">
            <Form.Check
                type="switch"
                id="price-hta-contract-as-hta-switch"
                checked={props.erpIntegration.erpConfiguration?.priceHtaContractAsHta}
                onChange={ev => props.onUpdate({...props.erpIntegration.erpConfiguration, priceHtaContractAsHta: ev.target.checked})}
            />
        </div>
        <div className="col">
            <Form.Check
                type="switch"
                id="price-basis-contract-as-basis-switch"
                checked={props.erpIntegration.erpConfiguration?.priceBasisContractAsBasis}
                onChange={ev => props.onUpdate({...props.erpIntegration.erpConfiguration, priceBasisContractAsBasis: ev.target.checked})}
            />
        </div>
        <div className="col">
            <Form.Check
                type="switch"
                id="price-dp-contract-as-dp-switch"
                checked={props.erpIntegration.erpConfiguration?.priceDelayedPriceContractAsDelayedPrice}
                onChange={ev => props.onUpdate({...props.erpIntegration.erpConfiguration, priceDelayedPriceContractAsDelayedPrice: ev.target.checked})}
            />
        </div>
    </div>
</div>
}


const IncludeFeesInFinalPriceForContractTypes: React.FC<ErpContractConfigurationProps> = props => {
    return <div>
    <div className="row">
        <div className="col-4"></div>
        <div className="col"><strong>HTA</strong></div>
        <div className="col"><strong>Basis</strong></div>
        <div className="col"><strong>Cash</strong></div>
    </div>
    <div className="row">
        <div className="col-4">
            <strong>Include Fees in Final Price</strong>
            <OverlayTrigger placement="right" overlay={IncludeFeesInFinalPricePopover}>
                <i className="fa fa-info-circle pl-2"></i>
            </OverlayTrigger>
        </div>
        <div className="col">
            <Form.Check
                type="switch"
                id="include-fees-in-final-price-for-hta-switch"
                checked={props.erpIntegration.erpConfiguration?.includeFeesInFinalPriceForHta}
                onChange={ev => props.onUpdate({...props.erpIntegration.erpConfiguration, includeFeesInFinalPriceForHta: ev.target.checked})}
            />
        </div>
        <div className="col">
            <Form.Check
                type="switch"
                id="include-fees-in-final-price-for-basis-switch"
                checked={props.erpIntegration.erpConfiguration?.includeFeesInFinalPriceForBasis}
                onChange={ev => props.onUpdate({...props.erpIntegration.erpConfiguration, includeFeesInFinalPriceForBasis: ev.target.checked})}
            />
        </div>
        <div className="col">
            <Form.Check
                type="switch"
                id="include-fees-in-final-price-for-cash-switch"
                checked={props.erpIntegration.erpConfiguration?.includeFeesInFinalPriceForCash}
                onChange={ev => props.onUpdate({...props.erpIntegration.erpConfiguration, includeFeesInFinalPriceForCash: ev.target.checked})}
            />
        </div>
    </div>
</div>
}

const PollingPopover = (
    <Popover id="polling-popover">
        <Popover.Content>
            <p>Reaching out to the ERP to request new data</p>
        </Popover.Content>
    </Popover>
);

const PriceAsSameTypePopover = (
    <Popover id="price-as-same-type-popover">
        <Popover.Content>
            <p>When enabled, priced contracts will use the parent contract type</p>
            <p>For example, if Basis is enabled, pricing a Basis contract would create a new contract with type "Basis" in the ERP</p>
            <p>If disabled, priced contracts are created with type "Cash" in the ERP</p>
        </Popover.Content>
    </Popover>
);

const IncludeFeesInFinalPricePopover = (
    <Popover id="inlcude-fees-in-final-price-popover">
        <Popover.Content>
            <p>When enabled, fees will be included in the final price sent to ERP</p>
            <p>For example, if HTA is enabled, HTA fee must be included with HTA contract price in the ERP</p>
            <p>If disabled, contract price wont include any fees in the ERP</p>
        </Popover.Content>
    </Popover>
);

const CustomerTypesPopover = (
    <Popover id="customer-types-popover">
        <Popover.Content>
            <p>Add a comma separated list for allowed customer types from ERP, like "F,C,S". Blank means all customer types allowed.</p>
        </Popover.Content>
    </Popover>
)

interface FormDetails {
    showImportData: boolean;
    showErpSystem: boolean;
    showContractImport: boolean;
    showContractPolling: boolean;
    showCommodityBalancePolling: boolean;
    showScaleTicketPolling: boolean;
    showSettlementPolling: boolean;
    showAccountPolling: boolean;
    pollingFrequencyStep?: number;
    showHedgeOrderExport: boolean;
    showExcludeContractsBeforeDate: boolean;
    showCustomerTypes: boolean;
    showInventoryLocationEditable: boolean;
    showContractExportThrottling: boolean;
}

export default ManageErpIntegration;